import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Input,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@roger/ui-components';
import {
  ChevronDown,
  ChevronRight,
  Presentation,
  Loader2,
  MoreHorizontal,
  Play,
  RefreshCw,
  RotateCcw,
} from 'lucide-react';
import {
  listDemoEnvironments,
  runDemoEnvironment,
  updateDemoEnvironment,
} from '../../services/api';
import type {
  DemoEnvironmentResponse,
  DemoEnvironmentRunResponse,
} from '../../services/api/types.gen';
import { getBusinessErrorMessage } from '../../utils/apiErrorDetail';
import { getHttpStatus } from '../../utils/httpErrorUtils';
import { formatDateInPT } from '../../utils/dateUtils';
import {
  DEMO_ENVIRONMENT_STATUS_PRESENTATION,
  compareByUrgency,
  distinctSystemName,
  refreshToggleHint,
  resolveSettings,
} from './demoEnvironmentStatus';
import { DEFAULT_DEMO_FF_TEMPLATE_AGENCY } from './demoAgencySetupDefaults';
import { inferDemoAgencySetupStageLabel } from './demoAgencySetupUi';
import { DemoEnvironmentFormDialog } from './DemoEnvironmentFormDialog';
import { formatPullSummary, usePullKinnser } from './usePullKinnser';
import { DemoEnvironmentRunDetails } from './DemoEnvironmentRunDetails';
import { runLabel } from './demoRunSteps';

/**
 * Every Kinnser agency, filtered to the ones an operator is working with.
 *
 * Every Kinnser agency is already a usable demo — it can be logged into and it
 * has data — so there is nothing to "add". Running or editing an agency is what
 * puts it in "My sandboxes"; hiding takes it back out while keeping its saved
 * settings. "All agencies" shows the rest, including hidden ones, so nothing is
 * ever unreachable.
 *
 * A run happens on a worker, not in this tab. Each row expands to show its
 * runs, and the list polls while any run is in progress, so several agencies
 * can run at once and closing the tab loses nothing.
 */

type Filter = 'mine' | 'all';

// Often enough that a step appears soon after it finishes; steps take seconds
// to minutes.
const RUN_POLL_INTERVAL_MS = 3000;

const COLUMN_COUNT = 7;

// Room kept under the table for one open row's run details.
const RESERVED_DETAILS_REM = 26;

function formatMoment(value: string | null | undefined): string {
  if (!value) return '—';
  return formatDateInPT(value);
}

function displayName(environment: DemoEnvironmentResponse): string {
  return environment.agency_written_name || environment.agency_system_name;
}

/** Run or edited, and not hidden — the agencies somebody is working with. */
function isMine(environment: DemoEnvironmentResponse): boolean {
  return environment.has_config && !environment.is_hidden;
}

function isRunning(environment: DemoEnvironmentResponse): boolean {
  return environment.latest_run?.status === 'running';
}

function listGroup(environment: DemoEnvironmentResponse): number {
  if (environment.is_hidden) return 2;
  return environment.has_config ? 0 : 1;
}

export const DemoEnvironmentsList: React.FC = () => {
  const stageLabel = useMemo(() => inferDemoAgencySetupStageLabel(), []);
  // Only demo has the nightly refresh schedule; elsewhere the toggle is
  // disabled rather than promising something nothing keeps.
  const refreshScheduled = stageLabel === 'demo';
  const [environments, setEnvironments] = useState<DemoEnvironmentResponse[]>(
    []
  );
  // Only the first load blanks the screen; polling must not collapse the
  // expanded rows.
  const [initialLoading, setInitialLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  // Chosen after the first load: until then nobody knows whether there is
  // anything in "My sandboxes" to show.
  const [filter, setFilter] = useState<Filter | null>(null);
  const [search, setSearch] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [editing, setEditing] = useState<DemoEnvironmentResponse | null>(null);
  const [pendingRun, setPendingRun] = useState<DemoEnvironmentResponse | null>(
    null
  );
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [pendingHide, setPendingHide] =
    useState<DemoEnvironmentResponse | null>(null);
  const [updateFailed, setUpdateFailed] = useState(false);

  // Polls do not wait for each other, so a slow response can land after a
  // newer one. An answer older than one already applied is dropped. Comparing
  // with the newest request instead would drop every answer once each takes
  // longer than the poll interval.
  const latestLoad = useRef(0);
  const appliedLoad = useRef(0);

  const load = useCallback(async () => {
    const thisLoad = ++latestLoad.current;
    try {
      const res = await listDemoEnvironments({ throwOnError: true });
      if (thisLoad < appliedLoad.current) return;
      appliedLoad.current = thisLoad;
      const rows = [...(res.data?.environments ?? [])];
      setEnvironments(rows);
      // Land on "All agencies" when nothing has been touched yet, so the first
      // visit is not an empty list with no obvious next step.
      setFilter(current => current ?? (rows.some(isMine) ? 'mine' : 'all'));
      setLoadFailed(false);
    } catch (error) {
      if (thisLoad < appliedLoad.current) return;
      console.error('Failed to load demo sandboxes', error);
      setLoadFailed(true);
    } finally {
      setInitialLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Poll only while something is running; at rest the list does not refetch.
  const anyRunning = environments.some(isRunning);
  useEffect(() => {
    if (!anyRunning) return;
    const timer = window.setInterval(() => void load(), RUN_POLL_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [anyRunning, load]);

  const toggleExpanded = (agencyId: string) =>
    setExpanded(current => {
      const next = new Set(current);
      if (next.has(agencyId)) next.delete(agencyId);
      else next.add(agencyId);
      return next;
    });

  const activeFilter: Filter = filter ?? 'mine';

  const visible = useMemo(() => {
    const query = search.trim().toLowerCase();
    return environments
      .filter(row => activeFilter === 'all' || isMine(row))
      .filter(
        row =>
          !query ||
          displayName(row).toLowerCase().includes(query) ||
          row.agency_system_name.toLowerCase().includes(query)
      )
      .sort((a, b) => {
        // Yours first, then untouched, then hidden. Without grouping, hundreds
        // of untouched "Never run" rows would sort above the sandboxes somebody
        // is actually using, because Never run outranks Idle and Active.
        const group = listGroup(a) - listGroup(b);
        if (group !== 0) return group;
        const urgency = compareByUrgency(a.status, b.status);
        if (urgency !== 0) return urgency;
        return displayName(a).localeCompare(displayName(b));
      });
  }, [environments, activeFilter, search]);

  const mineCount = useMemo(
    () => environments.filter(isMine).length,
    [environments]
  );

  const setHidden = async (
    environment: DemoEnvironmentResponse,
    isHidden: boolean
  ) => {
    setBusyId(environment.agency_id);
    setUpdateFailed(false);
    try {
      await updateDemoEnvironment({
        path: { agency_id: environment.agency_id },
        body: { is_hidden: isHidden },
        throwOnError: true,
      });
      setPendingHide(null);
      await load();
    } catch (error) {
      console.error('Failed to update demo sandbox', error);
      setUpdateFailed(true);
    } finally {
      setBusyId(null);
    }
  };

  const setRefreshNightly = async (
    environment: DemoEnvironmentResponse,
    refreshNightly: boolean
  ) => {
    setBusyId(environment.agency_id);
    setUpdateFailed(false);
    try {
      await updateDemoEnvironment({
        path: { agency_id: environment.agency_id },
        // The server replaces the whole saved block, so send it all back with
        // only this setting changed.
        body: {
          settings: {
            ...environment.settings,
            refresh_nightly: refreshNightly,
          },
        },
        throwOnError: true,
      });
      await load();
    } catch (error) {
      console.error('Failed to update demo sandbox', error);
      setUpdateFailed(true);
    } finally {
      setBusyId(null);
    }
  };

  if (initialLoading) {
    return (
      <div className='flex items-center gap-2 text-sm text-muted-foreground'>
        <Loader2 className='h-4 w-4 animate-spin' />
        Loading demo sandboxes…
      </div>
    );
  }

  return (
    <TooltipProvider delayDuration={300}>
      <div className='space-y-3'>
        <div className='flex items-start justify-between gap-4'>
          <p className='flex items-center gap-2 text-sm text-muted-foreground'>
            <Presentation className='h-4 w-4 shrink-0' />
            Manage demo sandboxes for Kinnser-synced agencies. Non-prod only.
          </p>
          {/* Which environment every run on this tab lands in. */}
          <Badge variant='outline' className='shrink-0 font-mono text-xs'>
            {stageLabel.toUpperCase()}
          </Badge>
        </div>

        <div className='flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
          <div
            role='group'
            aria-label='Which agencies to show'
            className='inline-flex rounded-md border p-0.5'
          >
            <Button
              type='button'
              size='sm'
              variant={activeFilter === 'mine' ? 'secondary' : 'ghost'}
              aria-pressed={activeFilter === 'mine'}
              onClick={() => setFilter('mine')}
            >
              My sandboxes ({mineCount})
            </Button>
            <Button
              type='button'
              size='sm'
              variant={activeFilter === 'all' ? 'secondary' : 'ghost'}
              aria-pressed={activeFilter === 'all'}
              onClick={() => setFilter('all')}
            >
              All agencies ({environments.length})
            </Button>
          </div>
          <Input
            inputSize='sm'
            className='w-full sm:w-64'
            placeholder='Search agencies…'
            aria-label='Search agencies'
            value={search}
            onChange={event => setSearch(event.target.value)}
          />
        </div>

        {loadFailed && (
          <div className='flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between'>
            <p className='text-sm text-red-700'>
              Unable to load demo sandboxes. Please try again.
            </p>
            <Button size='sm' variant='outline' onClick={() => void load()}>
              Retry
            </Button>
          </div>
        )}

        {updateFailed && !pendingHide && (
          <p className='text-sm text-red-700'>
            Unable to update the sandbox. Please try again.
          </p>
        )}

        {visible.length === 0 ? (
          <p className='text-sm text-muted-foreground'>
            {search.trim()
              ? 'No agencies match that search.'
              : activeFilter === 'mine'
                ? 'No sandboxes yet. Run or edit an agency under All agencies to add it here.'
                : 'No Kinnser agencies found.'}
          </p>
        ) : (
          // Always as tall as the closed table plus a little more than one row's
          // run details, so opening a row fills that space instead of changing
          // the page height. Rows are a fixed h-16 under an h-10 header, plus a
          // 1px border each.
          <div
            className='overflow-x-auto'
            style={{
              minHeight: `calc(${2.5 + visible.length * 4 + RESERVED_DETAILS_REM}rem + ${visible.length + 1}px)`,
            }}
          >
            {/* Scrolls inside itself rather than collapsing into tall wrapped
              rows at narrow widths. */}
            {/* Fixed layout so columns hold their width on both views; automatic
              layout sizes them to whatever rows are showing, and the two views
              show different rows. Last run takes the remaining width. */}
            <Table className='min-w-[848px] table-fixed'>
              <TableHeader>
                <TableRow>
                  <TableHead className='w-10'>
                    <span className='sr-only'>Expand</span>
                  </TableHead>
                  <TableHead className='w-[28%]'>Agency</TableHead>
                  <TableHead className='w-28'>Status</TableHead>
                  <TableHead className='w-28'>Last set up</TableHead>
                  <TableHead>Last run</TableHead>
                  <TableHead className='w-32 text-center'>
                    Nightly refresh
                  </TableHead>
                  <TableHead className='w-44 text-right'>
                    <span className='sr-only'>Actions</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {visible.map(environment => {
                  const presentation =
                    DEMO_ENVIRONMENT_STATUS_PRESENTATION[environment.status];
                  const refreshOn = resolveSettings(
                    environment.settings,
                    DEFAULT_DEMO_FF_TEMPLATE_AGENCY
                  ).refresh_nightly;
                  const refreshHint = refreshToggleHint({
                    scheduled: refreshScheduled,
                    on: refreshOn,
                    setUp: environment.setup_at != null,
                  });
                  const isBusy = busyId === environment.agency_id;
                  const name = displayName(environment);
                  const systemName = distinctSystemName(
                    environment.agency_written_name,
                    environment.agency_system_name
                  );
                  const isOpen = expanded.has(environment.agency_id);
                  const running = isRunning(environment);
                  return (
                    <React.Fragment key={environment.agency_id}>
                      {/* Fixed height, and every cell truncates to at most two
                        lines, so a long summary does not make one row taller
                        than the rest. */}
                      {/* The whole row toggles the run details. The actions
                        cell stops its clicks here, and so do the menu items
                        it portals out: React bubbles portal events through
                        the component tree. */}
                      <TableRow
                        className='h-16 cursor-pointer'
                        tabIndex={0}
                        aria-expanded={isOpen}
                        aria-label={`${isOpen ? 'Hide' : 'Show'} runs for ${name}`}
                        onClick={() => toggleExpanded(environment.agency_id)}
                        onKeyDown={event => {
                          if (event.target !== event.currentTarget) return;
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            toggleExpanded(environment.agency_id);
                          }
                        }}
                      >
                        <TableCell>
                          {isOpen ? (
                            <ChevronDown className='h-4 w-4 text-muted-foreground' />
                          ) : (
                            <ChevronRight className='h-4 w-4 text-muted-foreground' />
                          )}
                        </TableCell>
                        <TableCell>
                          <div className='truncate font-medium' title={name}>
                            {name}
                          </div>
                          {systemName && (
                            <div
                              className='truncate font-mono text-xs text-muted-foreground'
                              title={systemName}
                            >
                              {systemName}
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant='outline'
                            title={presentation.hint}
                            className={`whitespace-nowrap ${presentation.className}`}
                          >
                            {presentation.label}
                          </Badge>
                        </TableCell>
                        <TableCell className='whitespace-nowrap'>
                          {formatMoment(environment.setup_at)}
                        </TableCell>
                        <TableCell>
                          <LastRun run={environment.latest_run ?? null} />
                        </TableCell>
                        {/* Stops its clicks like the actions cell, so the toggle
                          does not also open the row. */}
                        <TableCell
                          className='cursor-default text-center'
                          onClick={event => event.stopPropagation()}
                        >
                          {/* Hidden rows are retired, so they offer only Unhide. */}
                          {!environment.is_hidden && (
                            <Tooltip>
                              {/* The span carries the tooltip, because a
                                disabled button gets no pointer events. */}
                              <TooltipTrigger asChild>
                                <span className='inline-flex'>
                                  <Button
                                    type='button'
                                    variant='ghost'
                                    size='sm'
                                    aria-label={`Nightly refresh for ${name}`}
                                    aria-pressed={refreshOn}
                                    disabled={!refreshScheduled || isBusy}
                                    onClick={() =>
                                      void setRefreshNightly(
                                        environment,
                                        !refreshOn
                                      )
                                    }
                                  >
                                    <RefreshCw
                                      className={`h-4 w-4 ${
                                        refreshScheduled && refreshOn
                                          ? 'text-green-700'
                                          : 'text-muted-foreground/50'
                                      }`}
                                    />
                                  </Button>
                                </span>
                              </TooltipTrigger>
                              <TooltipContent className='max-w-[260px]'>
                                {refreshHint}
                              </TooltipContent>
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell
                          className='cursor-default text-right'
                          onClick={event => event.stopPropagation()}
                        >
                          <div className='flex justify-end gap-1'>
                            {environment.is_hidden ? (
                              <Button
                                type='button'
                                variant='outline'
                                size='sm'
                                aria-label={`Unhide ${name}`}
                                disabled={isBusy}
                                onClick={() =>
                                  void setHidden(environment, false)
                                }
                              >
                                <RotateCcw className='mr-1 h-3.5 w-3.5' />
                                Unhide
                              </Button>
                            ) : (
                              <>
                                <Button
                                  type='button'
                                  size='sm'
                                  aria-label={`Run ${name}`}
                                  disabled={isBusy || running}
                                  onClick={() => setPendingRun(environment)}
                                >
                                  {running ? (
                                    <Loader2 className='mr-1 h-3.5 w-3.5 animate-spin' />
                                  ) : (
                                    <Play className='mr-1 h-3.5 w-3.5' />
                                  )}
                                  {running ? 'Running' : 'Run'}
                                </Button>
                                {/* Not modal: a modal menu that opens a dialog
                                can leave pointer-events stuck off the page
                                once the dialog closes. */}
                                <DropdownMenu modal={false}>
                                  <DropdownMenuTrigger asChild>
                                    <Button
                                      type='button'
                                      variant='ghost'
                                      size='sm'
                                      aria-label={`More actions for ${name}`}
                                      disabled={isBusy}
                                    >
                                      <MoreHorizontal className='h-4 w-4' />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align='end'>
                                    <DropdownMenuItem
                                      onSelect={() => setEditing(environment)}
                                    >
                                      Edit settings
                                    </DropdownMenuItem>
                                    {/* Hiding only means something once the agency
                                    is in "My sandboxes". */}
                                    {environment.has_config && (
                                      <DropdownMenuItem
                                        className='text-destructive focus:text-destructive'
                                        onSelect={() =>
                                          setPendingHide(environment)
                                        }
                                      >
                                        Hide
                                      </DropdownMenuItem>
                                    )}
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                      {isOpen && (
                        <TableRow>
                          <TableCell
                            colSpan={COLUMN_COUNT}
                            // Lines up with the Agency column.
                            className='bg-muted/30 pl-[3.25rem]'
                          >
                            <DemoEnvironmentRunDetails
                              environment={environment}
                            />
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}

        <DemoEnvironmentFormDialog
          environment={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void load();
          }}
          onPulled={() => void load()}
        />

        <DemoEnvironmentRunConfirmDialog
          environment={pendingRun}
          onClose={() => setPendingRun(null)}
          onStarted={agencyId => {
            setPendingRun(null);
            // Open the row so the operator sees the run start.
            setExpanded(current => new Set(current).add(agencyId));
            void load();
          }}
          onAlreadyRunning={() => void load()}
          onPulled={() => void load()}
        />

        <DemoEnvironmentHideDialog
          environment={pendingHide}
          busy={busyId !== null}
          failed={updateFailed}
          onCancel={() => {
            setPendingHide(null);
            setUpdateFailed(false);
          }}
          onConfirm={() => {
            if (pendingHide) void setHidden(pendingHide, true);
          }}
        />
      </div>
    </TooltipProvider>
  );
};

/** The latest run in at most two lines; the steps are in the expanded row. */
const LastRun: React.FC<{ run: DemoEnvironmentRunResponse | null }> = ({
  run,
}) => {
  if (!run) return <span className='text-muted-foreground'>—</span>;
  return (
    <div className='flex min-w-0 flex-col gap-0.5'>
      <span
        className={`truncate ${
          run.status === 'failed'
            ? 'font-medium text-red-700'
            : 'text-muted-foreground'
        }`}
      >
        {runLabel(run)}{' '}
        {run.status === 'running'
          ? `since ${formatMoment(run.started_at)}`
          : formatMoment(run.finished_at ?? run.started_at)}
      </span>
      {run.summary && (
        <span
          className='truncate text-xs text-muted-foreground'
          title={run.summary}
        >
          {run.summary}
        </span>
      )}
    </div>
  );
};

// Confirm first because a run wipes the agency's test patients and re-seeds it;
// one click from a list is too easy for something that destructive. The dialog
// closes as soon as the run starts: the run happens on a worker, and its
// progress shows in the expanded row.
//
// A run builds only on data already pulled from Kinnser, so for an agency with
// no clinics the dialog offers the pull first and Run once it has succeeded —
// first setup stays in one dialog rather than a refusal and a trip elsewhere.
const DemoEnvironmentRunConfirmDialog: React.FC<{
  environment: DemoEnvironmentResponse | null;
  onClose: () => void;
  onStarted: (agencyId: string) => void;
  onAlreadyRunning: () => void;
  onPulled: () => void;
}> = ({ environment, onClose, onStarted, onAlreadyRunning, onPulled }) => {
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const {
    pull,
    pulling,
    result: pullResult,
    error: pullError,
    reset: resetPull,
  } = usePullKinnser();

  useEffect(() => {
    if (environment === null) {
      setError(null);
      resetPull();
    }
  }, [environment, resetPull]);

  // The environment is a snapshot taken when the dialog opened, so a pull made
  // here is read from its own result rather than a refreshed list row. A pull
  // that found no clinics leaves nothing to build, so Run stays unavailable.
  const latestPull = pullResult ?? environment?.kinnser_pull ?? null;
  const hasClinics = (latestPull?.clinics ?? 0) > 0;
  const busy = starting || pulling;

  const start = async () => {
    if (!environment) return;
    setStarting(true);
    setError(null);
    try {
      await runDemoEnvironment({
        path: { agency_id: environment.agency_id },
        throwOnError: true,
      });
      onStarted(environment.agency_id);
    } catch (caught) {
      console.error('Failed to start demo setup', caught);
      // Somebody else started it; refresh so the row shows that run.
      if (getHttpStatus(caught) === 409) onAlreadyRunning();
      setError(
        getBusinessErrorMessage(caught) ??
          'Unable to start the run. Please try again.'
      );
    } finally {
      setStarting(false);
    }
  };

  return (
    <Dialog
      open={environment !== null}
      onOpenChange={open => {
        if (!open && !busy) onClose();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            Run demo setup for {environment ? displayName(environment) : ''}?
          </DialogTitle>
          <DialogDescription>
            This deletes the agency’s existing test patients and seeds it again
            from the saved settings. It runs in the background and takes several
            minutes; you can close this page.
          </DialogDescription>
        </DialogHeader>
        {latestPull && (
          <p className='text-sm text-muted-foreground'>
            {formatPullSummary(latestPull)}
          </p>
        )}
        {!hasClinics && (
          <p className='text-sm'>
            {latestPull
              ? 'The last pull found no clinics. Pull again once the agency has one in Kinnser.'
              : 'This agency has not been pulled from Kinnser yet. Pull it first.'}
          </p>
        )}
        {pullError && <p className='text-sm text-red-700'>{pullError}</p>}
        {error && <p className='text-sm text-red-700'>{error}</p>}
        <DialogFooter>
          <Button
            type='button'
            variant='outline'
            onClick={onClose}
            disabled={busy}
          >
            Cancel
          </Button>
          {hasClinics ? (
            <Button type='button' onClick={() => void start()} disabled={busy}>
              {starting && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
              Run setup
            </Button>
          ) : (
            <Button
              type='button'
              disabled={busy || !environment}
              onClick={async () => {
                if (!environment) return;
                if (await pull(environment.agency_id)) onPulled();
              }}
            >
              {pulling && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
              Pull from Kinnser
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Colocated: it is a few lines of copy and one destructive button, and the copy
// is the part that matters — an operator must not expect Hide to tear the
// seeded agency down.
const DemoEnvironmentHideDialog: React.FC<{
  environment: DemoEnvironmentResponse | null;
  busy: boolean;
  failed: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}> = ({ environment, busy, failed, onCancel, onConfirm }) => (
  <Dialog
    open={environment !== null}
    onOpenChange={open => {
      if (!open) onCancel();
    }}
  >
    <DialogContent>
      <DialogHeader>
        <DialogTitle>
          Hide {environment ? displayName(environment) : ''}?
        </DialogTitle>
        <DialogDescription>
          It leaves My sandboxes. The agency and everything already seeded into
          it are left alone, and the saved settings are kept — unhide it from
          All agencies.
        </DialogDescription>
      </DialogHeader>
      {failed && (
        <p className='text-sm text-red-700'>
          Unable to hide the sandbox. Please try again.
        </p>
      )}
      <DialogFooter>
        <Button
          type='button'
          variant='outline'
          onClick={onCancel}
          disabled={busy}
        >
          Cancel
        </Button>
        <Button
          type='button'
          variant='destructive'
          onClick={onConfirm}
          disabled={busy}
        >
          {busy && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
          Hide
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export default DemoEnvironmentsList;
